import { useEffect, useRef } from 'react';
import { Routes, Route, useParams, useLocation } from 'react-router-dom';
import type { CSSProperties, MouseEvent } from 'react';
import Nav from './sections/Nav.tsx';
import Hero from './sections/Hero.tsx';
import FeatureBand from './sections/FeatureBand.tsx';
import Shift from './sections/Shift.tsx';
import WhoWeAre from './sections/WhoWeAre.tsx';
import Vision from './sections/Vision.tsx';
import Approach from './sections/Approach.tsx';
import ServicesFull from './sections/ServicesFull.tsx';
import Industries from './sections/Industries.tsx';
import Principles from './sections/Principles.tsx';
import SoftwareFactory from './sections/SoftwareFactory.tsx';
import WhyIndependent from './sections/WhyIndependent.tsx';
import HowWeWork from './sections/HowWeWork.tsx';
import Closing from './sections/Closing.tsx';
import Contact from './sections/Contact.tsx';
import Footer from './sections/Footer.tsx';
import type { Tweaks } from './types.ts';
import { SERVICES } from './data/services.ts';
import ServicePage from './pages/services/ServicePage.tsx';
import AboutPage from './pages/company/AboutPage.tsx';
import ApproachPage from './pages/company/ApproachPage.tsx';
import PrinciplesPage from './pages/company/PrinciplesPage.tsx';
import IndustriesPage from './pages/company/IndustriesPage.tsx';
import ConsultantsPage from './pages/company/ConsultantsPage.tsx';
import ContactPage from './pages/company/ContactPage.tsx';
import StackAuditPage from './pages/company/StackAuditPage.tsx';
import TheConflictPage from './pages/company/TheConflictPage.tsx';
import NotFound from './pages/NotFound.tsx';

const TWEAKS: Tweaks = {
  heroStar: 'glow',
  glow: 16,
  starfield: 55,
  whatWeDo: 'grid',
  ledger: 'two-col',
  bands: 'alternate',
  density: 'airy',
  headingScale: 100,
  accent: ['#C6A15B', '#DCBD82', '#9C7C3E'],
};

const DENSITY_PY: Record<Tweaks['density'], string> = {
  airy: 'clamp(5rem, 11vw, 11rem)',
  regular: 'clamp(4rem, 9vw, 8rem)',
  tight: 'clamp(3rem, 6vw, 5.5rem)',
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  const n = parseInt(x, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function HomeContent() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Scroll-reveal observer.
  useEffect(() => {
    const scope = rootRef.current;
    if (!scope) return;
    scope.classList.add('reveal-on');
    const els = scope.querySelectorAll<HTMLElement>('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Re-apply the URL hash after first paint so CTAs from subpages land on the right section.
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  }, []);

  const onNav = (e: MouseEvent<HTMLAnchorElement>) => {
    const href = e.currentTarget.getAttribute('href');
    if (!href || !href.startsWith('#')) return;
    const el = document.getElementById(href.slice(1));
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', href);
  };

  const [a, aLight, aDeep] = TWEAKS.accent;
  const [r, g, b] = hexToRgb(a);
  const rgb = `${r}, ${g}, ${b}`;
  const styleVars: CSSProperties = {
    ['--hero-glow' as string]: TWEAKS.glow / 100,
    ['--field-opacity' as string]: TWEAKS.starfield / 100,
    ['--heading-scale' as string]: TWEAKS.headingScale / 100,
    ['--section-py' as string]: DENSITY_PY[TWEAKS.density],
    ['--color-gold' as string]: a,
    ['--color-gold-light' as string]: aLight,
    ['--color-gold-deep' as string]: aDeep,
    ['--color-gold-a08' as string]: `rgba(${rgb}, 0.08)`,
    ['--color-gold-a16' as string]: `rgba(${rgb}, 0.16)`,
    ['--color-gold-a32' as string]: `rgba(${rgb}, 0.32)`,
  };

  return (
    <div className={`pl-site bands-${TWEAKS.bands}`} ref={rootRef} style={styleVars}>
      <Nav onNav={onNav} />
      <main id="main-content">
        <Hero t={TWEAKS} />
        <FeatureBand />
        <Shift />
        <WhoWeAre />
        <Vision />
        <Approach />
        <ServicesFull />
        <Industries />
        <Principles />
        <SoftwareFactory />
        <WhyIndependent t={TWEAKS} />
        <HowWeWork />
        <Closing />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function ServicePageRoute() {
  const { slug } = useParams<{ slug: string }>();
  const data = SERVICES.find(s => s.slug === slug);
  if (!data) return <NotFound />;
  return <ServicePage data={data} />;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<HomeContent />} />
      <Route path="/services/:slug" element={<ServicePageRoute />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/approach" element={<ApproachPage />} />
      <Route path="/principles" element={<PrinciplesPage />} />
      <Route path="/industries" element={<IndustriesPage />} />
      <Route path="/consultants" element={<ConsultantsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/stack-audit" element={<StackAuditPage />} />
      <Route path="/the-conflict" element={<TheConflictPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}
